import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { ClerkProvider } from "@clerk/clerk-react";
import GlobalStyles from "@mui/material/GlobalStyles";
import { StyledEngineProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PostHogProvider } from "./components/PostHogProvider";
import { loadPublicEnv, PUBLIC_ENV } from "./config/publicEnv";
import { RoleProvider } from "./context/RoleContext";
import { initGtm } from "./lib/gtm";
import { initPostHog } from "./lib/posthog";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "staging") {
  const axe = await import("@axe-core/react");
  axe.default(React, ReactDOM, 1000);
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const root = ReactDOM.createRoot(rootElement);

/**
 * Public config is fetched from GET /api/config before the tree mounts, because
 * <ClerkProvider> needs the publishable key at construction time. See config/publicEnv.ts
 * for why these are no longer bundle literals.
 *
 * A failure here is fatal — without a Clerk key nothing can authenticate — so we render
 * an explicit message rather than a blank white page.
 */
try {
  await loadPublicEnv();
} catch (error) {
  console.error("Startup failed:", error);
  root.render(
    <div
      style={{
        fontFamily: "system-ui, sans-serif",
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "1rem",
        textAlign: "center",
      }}
    >
      <div>
        <h1>Unable to start</h1>
        <p>The application could not load its configuration. Please try again shortly.</p>
      </div>
    </div>,
  );
  throw error;
}

initPostHog();
// Loads GTM only when GTM_CONTAINER_ID is configured (production and staging) — no-ops otherwise.
initGtm();

const PUBLISHABLE_KEY = PUBLIC_ENV.CLERK_PUBLISHABLE_KEY;

root.render(
  <React.StrictMode>
    <ClerkProvider
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      publishableKey={PUBLISHABLE_KEY}
      appearance={{
        elements: {
          impersonationFab: { display: "none !important" },
        },
      }}
    >
      <StyledEngineProvider enableCssLayer>
        <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />
        <QueryClientProvider client={queryClient}>
          <PostHogProvider>
            <RoleProvider>
              <App />
            </RoleProvider>
          </PostHogProvider>
        </QueryClientProvider>
      </StyledEngineProvider>
    </ClerkProvider>
  </React.StrictMode>,
);
